import apiService from './api';

class OrderService {
    // Crear nueva orden
    async createOrder(orderData) {
        try {
            return await apiService.post('/orders', orderData);
        } catch (error) {
            throw new Error(error.message || 'Error al crear la orden');
        }
    }

    // Obtener órdenes del usuario
    async getUserOrders(page = 1, limit = 10) {
        try {
            return await apiService.get('/orders', { page, limit });
        } catch (error) {
            throw new Error(error.message || 'Error al obtener órdenes');
        }
    }

    // Obtener orden por ID
    async getOrderById(orderId) {
        try {
            return await apiService.get(`/orders/${orderId}`);
        } catch (error) {
            throw new Error(error.message || 'Error al obtener la orden');
        }
    }

    // Actualizar estado de orden
    async updateOrderStatus(orderId, status) {
        try {
            return await apiService.put(`/orders/${orderId}/status`, { status });
        } catch (error) {
            throw new Error(error.message || 'Error al actualizar estado de la orden');
        }
    }

    // Cancelar orden
    async cancelOrder(orderId, reason) {
        try {
            return await apiService.put(`/orders/${orderId}/cancel`, { reason });
        } catch (error) {
            throw new Error(error.message || 'Error al cancelar la orden');
        }
    }

    // Obtener detalles de envío
    async getShippingDetails(orderId) {
        try {
            return await apiService.get(`/orders/${orderId}/shipping`);
        } catch (error) {
            throw new Error(error.message || 'Error al obtener detalles de envío');
        }
    }

    // Calcular costos de envío
    async calculateShipping(shippingData) {
        try {
            return await apiService.post('/orders/calculate-shipping', shippingData);
        } catch (error) {
            throw new Error(error.message || 'Error al calcular envío');
        }
    }

    // Procesar pago
    async processPayment(paymentData) {
        try {
            return await apiService.post('/orders/process-payment', paymentData);
        } catch (error) {
            throw new Error(error.message || 'Error al procesar el pago');
        }
    }

    // Verificar estado de pago
    async checkPaymentStatus(orderId) {
        try {
            return await apiService.get(`/orders/${orderId}/payment-status`);
        } catch (error) {
            throw new Error(error.message || 'Error al verificar estado de pago');
        }
    }

    // Obtener factura
    async getInvoice(orderId) {
        try {
            return await apiService.get(`/orders/${orderId}/invoice`);
        } catch (error) {
            throw new Error(error.message || 'Error al obtener factura');
        }
    }

    // Descargar factura PDF
    async downloadInvoicePDF(orderId) {
        try {
            const response = await fetch(`${apiService.baseURL}/orders/${orderId}/invoice/pdf`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al descargar factura');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `factura-${orderId}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            return { success: true };
        } catch (error) {
            throw new Error(error.message || 'Error al descargar factura');
        }
    }

    // Solicitar devolución
    async requestReturn(orderId, returnData) {
        try {
            return await apiService.post(`/orders/${orderId}/return`, returnData);
        } catch (error) {
            throw new Error(error.message || 'Error al solicitar devolución');
        }
    }

    // Obtener historial de la orden
    async getOrderHistory(orderId) {
        try {
            return await apiService.get(`/orders/${orderId}/history`);
        } catch (error) {
            throw new Error(error.message || 'Error al obtener historial de la orden');
        }
    }

    // Rastrear orden
    async trackOrder(orderId) {
        try {
            return await apiService.get(`/orders/${orderId}/tracking`);
        } catch (error) {
            throw new Error(error.message || 'Error al rastrear la orden');
        }
    }

    // Obtener métodos de pago disponibles
    async getPaymentMethods() {
        try {
            return await apiService.get('/orders/payment-methods');
        } catch (error) {
            throw new Error(error.message || 'Error al obtener métodos de pago');
        }
    }

    // Obtener opciones de envío
    async getShippingOptions(address) {
        try {
            return await apiService.post('/orders/shipping-options', address);
        } catch (error) {
            throw new Error(error.message || 'Error al obtener opciones de envío');
        }
    }

    // Validar cupón de descuento
    async validateCoupon(couponCode, orderTotal) {
        try {
            return await apiService.post('/orders/validate-coupon', {
                code: couponCode,
                total: orderTotal
            });
        } catch (error) {
            throw new Error(error.message || 'Error al validar cupón');
        }
    }

    // Aplicar cupón de descuento
    async applyCoupon(orderId, couponCode) {
        try {
            return await apiService.post(`/orders/${orderId}/apply-coupon`, {
                code: couponCode
            });
        } catch (error) {
            throw new Error(error.message || 'Error al aplicar cupón');
        }
    }

    // Remover cupón de descuento
    async removeCoupon(orderId) {
        try {
            return await apiService.delete(`/orders/${orderId}/coupon`);
        } catch (error) {
            throw new Error(error.message || 'Error al remover cupón');
        }
    }

    // Obtener resumen de orden
    async getOrderSummary(cartItems, shippingAddress, couponCode = null) {
        try {
            return await apiService.post('/orders/summary', {
                items: cartItems,
                shipping_address: shippingAddress,
                coupon_code: couponCode
            });
        } catch (error) {
            throw new Error(error.message || 'Error al obtener resumen de orden');
        }
    }

    // Confirmar orden
    async confirmOrder(orderId) {
        try {
            return await apiService.post(`/orders/${orderId}/confirm`);
        } catch (error) {
            throw new Error(error.message || 'Error al confirmar orden');
        }
    }

    // Obtener estadísticas de órdenes del usuario
    async getUserOrderStats() {
        try {
            return await apiService.get('/orders/stats');
        } catch (error) {
            throw new Error(error.message || 'Error al obtener estadísticas de órdenes');
        }
    }
}

// Instancia singleton del servicio de órdenes
const orderService = new OrderService();

export default orderService;
